import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardShell } from "@/pages/Dashboard";
import { api } from "@/lib/api";
import { CARGO_LABELS, STATUS_LABELS, formatEUR, formatDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { InsuranceCalculator } from "@/components/InsuranceCalculator";
import { AlertDialog } from "@/components/AlertDialog";
import { Search, Package, ShieldCheck, Truck, BellPlus, Bell, Trash2 } from "lucide-react";
import { CARGO_LABELS as CL } from "@/lib/constants";
import { toast } from "sonner";

const statusColor = { pending: "bg-amber-500", accepted: "bg-emerald-600", rejected: "bg-destructive", completed: "bg-primary" };

export default function PrivateDashboard() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [insVal, setInsVal] = useState("");
  const [insOn, setInsOn] = useState(true);

  const load = () => api.get("/bookings/mine").then(({ data }) => setBookings(data)).catch(() => {});
  const loadAlerts = () => api.get("/alerts/mine").then(({ data }) => setAlerts(data)).catch(() => {});
  useEffect(() => { load(); loadAlerts(); }, []);

  const delAlert = async (id) => {
    try { await api.delete(`/alerts/${id}`); toast.success("Alert eliminato"); loadAlerts(); }
    catch { toast.error("Errore"); }
  };

  return (
    <DashboardShell title="Le mie spedizioni" subtitle="Gestisci le richieste di trasporto e assicura la merce."
      actions={<Button onClick={() => navigate("/cerca")} className="rounded-sm bg-accent hover:bg-accent/90 text-accent-foreground font-semibold gap-2" data-testid="dash-search-btn"><Search size={16} /> Cerca tratta</Button>}>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <h2 className="font-display font-semibold text-lg flex items-center gap-2"><Package size={18} /> Richieste inviate ({bookings.length})</h2>
          {bookings.length === 0 && (
            <div className="border border-dashed border-border rounded-sm p-10 text-center" data-testid="no-bookings">
              <Truck size={36} className="mx-auto text-muted-foreground" />
              <p className="font-display font-semibold mt-3">Nessuna spedizione</p>
              <p className="text-sm text-muted-foreground mt-1">Cerca una tratta e invia la tua prima richiesta.</p>
              <Button onClick={() => navigate("/cerca")} className="rounded-sm mt-4" data-testid="empty-search-btn">Cerca ora</Button>
            </div>
          )}
          {bookings.map((b) => (
            <div key={b.booking_id} className="border border-border rounded-sm p-5 bg-card" data-testid={`booking-${b.booking_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-display font-semibold">{b.route_summary}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{CARGO_LABELS[b.cargo_type] || b.cargo_type}{b.weight_kg ? ` · ${b.weight_kg} kg` : ""}</p>
                </div>
                <Badge className={`rounded-sm text-white ${statusColor[b.status]}`}>{STATUS_LABELS[b.status]}</Badge>
              </div>
              <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-muted-foreground">
                {b.offered_price ? <span>Offerta: <strong className="text-foreground">{formatEUR(b.offered_price)}</strong></span> : null}
                {(b.pickup_stop || b.dropoff_stop) && <span>{b.pickup_stop} → {b.dropoff_stop}</span>}
                {b.insurance_enabled && <span className="flex items-center gap-1 text-primary"><ShieldCheck size={14} /> Assicurata {formatEUR(b.insurance_premium)}</span>}
                <span>{formatDate(b.created_at)}</span>
              </div>
            </div>
          ))}
        </div>

        <div>
          <h2 className="font-display font-semibold text-lg mb-4 flex items-center gap-2"><ShieldCheck size={18} /> Preventivo assicurazione</h2>
          <InsuranceCalculator enabled={insOn} setEnabled={setInsOn} value={insVal} setValue={setInsVal} />
          <p className="text-xs text-muted-foreground mt-3">Simula il costo della polizza (3% del valore) prima di inviare una richiesta.</p>

          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-semibold text-lg flex items-center gap-2"><Bell size={18} /> I miei alert</h2>
              <AlertDialog onCreated={loadAlerts} trigger={
                <Button size="sm" variant="outline" className="rounded-sm gap-1.5" data-testid="dash-create-alert-btn"><BellPlus size={14} /> Nuovo</Button>
              } />
            </div>
            {alerts.length === 0 && (
              <p className="text-xs text-muted-foreground border border-dashed border-border rounded-sm p-4">Nessun alert attivo. Crea un alert per una tratta non disponibile e avviseremo i trasportatori compatibili.</p>
            )}
            <div className="space-y-2">
              {alerts.map((a) => (
                <div key={a.alert_id} className="border border-border rounded-sm p-3 bg-card flex items-start justify-between gap-2" data-testid={`alert-${a.alert_id}`}>
                  <div className="min-w-0">
                    <p className="font-display font-semibold text-sm truncate">{a.origin} → {a.destination}</p>
                    <p className="text-xs text-muted-foreground">{a.cargo_type ? (CL[a.cargo_type] || a.cargo_type) : "Merce generica"}{a.date_from ? ` · ${a.date_from}` : ""}</p>
                  </div>
                  <button onClick={() => delAlert(a.alert_id)} className="text-destructive hover:bg-destructive/10 rounded-sm p-1 shrink-0" data-testid={`delete-alert-${a.alert_id}`}><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}
